// ============================
// AuthContext.jsx COMPLETO
// ============================

import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// ============================
// Upsert: garantiza documento en usuarios/{email}
// ============================
async function ensureUserDoc(user) {
  try {
    const email = (user?.email || '').trim().toLowerCase();
    if (!email || !user?.uid) return;

    await setDoc(
      doc(db, 'usuarios', email),
      {
        uid: user.uid,
        correo: email,
        actualizadoEn: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[Auth] ensureUserDoc fallo:', e);
  }
}

// ============================
// Provider
// ============================
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeRole = null;
    let unsubscribePerfil = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      console.log('[Auth] onAuthStateChanged ->', currentUser?.uid || 'NO USER');

      // ============================
      // Usuario NO logueado
      // ============================
      if (!currentUser) {
        if (unsubscribeRole) unsubscribeRole();
        setUser(null);
        setLoading(false);
        return;
      }

      // Normalizar correo
      const emailId = (currentUser.email || '').trim().toLowerCase();

      // Crear doc base si no existe
      await ensureUserDoc(currentUser);

      // ✅ asegurar roles/{email} (si no existe, asumir user)
      try {
        const refRoleEmail = doc(db, "roles", emailId);
        const snapRoleEmail = await getDoc(refRoleEmail);

        if (!snapRoleEmail.exists()) {
          // 👇 fallback: si antes lo tenías guardado por uid, lo copiamos
          const refRoleUid = doc(db, "roles", currentUser.uid);
          const snapRoleUid = await getDoc(refRoleUid);

          if (snapRoleUid.exists()) {
            await setDoc(refRoleEmail, snapRoleUid.data(), { merge: true });
          } else {
            await setDoc(refRoleEmail, { user: true }, { merge: true });
          }
        }
      } catch (e) {
        console.warn("[Auth] asegurar roles/{email} falló:", e);
      }

      // ============================
      // Suscripción al PERFIL (usuarios/{email})
      // ============================
      if (unsubscribePerfil) unsubscribePerfil();

      const perfilRef = doc(db, 'usuarios', emailId);

      unsubscribePerfil = onSnapshot(
        perfilRef,
        (snap) => {
          const datos = snap.exists() ? snap.data() : {};

          const perfilIncompleto =
            !datos.nombres ||
            !datos.apellidos ||
            !datos.rut ||
            !datos.celular;

          setUser((prev) => ({
            ...prev,
            uid: currentUser.uid,
            email: currentUser.email,

            // 🔥 PERFIL SIEMPRE ACTUALIZADO
            ...datos,

            perfilIncompleto,
          }));

          console.log('[Auth] PERFIL actualizado => incompleto:', perfilIncompleto);
        },
        (err) => {
          console.warn('[Auth] Error escuchando perfil:', err);
        }
      );
        
// ============================
// Suscripción a roles
// ============================
if (unsubscribeRole) unsubscribeRole();

let firstRoleResolved = false;
const rolRef = doc(db, 'roles', currentUser.uid);
console.log('[Auth] Escuchando roles en:', `roles/${currentUser.uid}`);

unsubscribeRole = onSnapshot(
  rolRef,
  (snap) => {
    let rol = 'usuario';

    if (snap.exists()) {
      const r = snap.data() || {};
      console.log('[Auth] roles snapshot data =>', r);

      if (r.admin === true) {
        rol = 'admin';
      } else if (r.worker === true) {
        rol = 'worker';
      } else if (r.user === true) {
        rol = 'usuario';
      } else {
        rol = 'usuario';
      }
    } else {
      console.log('[Auth] roles snapshot: SIN DOC (usuario por defecto)');
      rol = 'usuario';
    }

    setUser((prev) => ({
      ...prev,
      rol,
    }));

    console.log('[Auth] user.rol =>', rol);

    if (!firstRoleResolved) {
      setLoading(false);
      firstRoleResolved = true;
    }
  },
  (err) => {
    console.warn('[Auth] Error al escuchar roles:', err);

    setUser((prev) => ({
      ...prev,
      rol: 'usuario',
    }));

    if (!firstRoleResolved) {
      setLoading(false);
      firstRoleResolved = true;
    }
  }
);
    });

      return () => {
        if (unsubscribeRole) unsubscribeRole();
        if (unsubscribePerfil) unsubscribePerfil();
        unsubscribeAuth();
      };
  }, []);

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
